
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

export interface Favorite {
  id: string;
  test_id: string;
  test_type: string;
  created_at: string;
}

export const useFavorites = () => {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Use useCallback to memoize the fetch function
  const fetchFavorites = useCallback(async () => {
    try {
      console.log("Fetching favorites...");
      setIsLoading(true);
      const { data: session } = await supabase.auth.getSession();

      if (!session.session) {
        console.log("No active session, clearing favorites");
        setFavorites([]);
        return;
      }

      const { data, error } = await supabase
        .from("user_favorite_tests")
        .select("*");

      if (error) throw error;

      console.log("Favorites fetched:", data?.length || 0, "items");
      setFavorites(data || []);
    } catch (error: any) {
      console.error("Error fetching favorites:", error.message);
      toast({
        variant: "destructive",
        title: "Erreur de chargement",
        description: "Impossible de charger vos favoris",
      });
    } finally {
      setIsLoading(false);
    }
  }, []); // Removed toast dependency to prevent infinite loops

  const addFavorite = async (testId: string, testType: string) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        toast({
          variant: "destructive",
          title: "Non connecté",
          description: "Vous devez être connecté pour ajouter un favori",
        });
        return null;
      }
      
      // Check if favorite already exists
      const existingFavorite = favorites.find(
        fav => fav.test_id === testId && fav.test_type === testType
      );
      
      if (existingFavorite) {
        console.log("Favorite already exists, skipping insert");
        toast({
          title: "Déjà en favori",
          description: "Ce test est déjà dans vos favoris",
        });
        return existingFavorite;
      }
      
      console.log(`Adding favorite: ${testId} (${testType})`);
      const { data, error } = await supabase
        .from("user_favorite_tests")
        .insert({
          test_id: testId,
          test_type: testType,
          user_id: session.session.user.id
        })
        .select();

      if (error) {
        // If it's a duplicate key error, silently handle it
        if (error.code === '23505') {
          console.log("Duplicate favorite detected, fetching existing");
          await fetchFavorites();
          return null;
        }
        throw error;
      }

      // Update local state immediately
      console.log("Favorite added successfully:", data[0]);
      setFavorites((prev) => [...prev, data[0]]);
      
      toast({
        title: "Favori ajouté",
        description: "Test ajouté à vos favoris",
      });
      
      // Force a refresh to ensure we have the latest data
      fetchFavorites();
      
      return data[0];
    } catch (error: any) {
      console.error("Error adding favorite:", error.message);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Une erreur est survenue lors de l'ajout du favori",
      });
      return null;
    }
  };

  const removeFavorite = async (id: string) => {
    try {
      console.log(`Removing favorite: ${id}`);
      const { error } = await supabase
        .from("user_favorite_tests")
        .delete()
        .eq("id", id);

      if (error) throw error;

      // Update local state immediately
      console.log("Favorite removed successfully");
      setFavorites((prev) => prev.filter((fav) => fav.id !== id));
      
      toast({
        title: "Favori supprimé",
        description: "Test retiré de vos favoris",
      });
      
      // Force a refresh to ensure we have the latest data
      fetchFavorites();
    } catch (error: any) {
      console.error("Error removing favorite:", error.message);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message,
      });
    }
  };

  const isFavorite = (testId: string) => {
    return favorites.some((fav) => fav.test_id === testId);
  };

  const getFavoriteId = (testId: string) => {
    const favorite = favorites.find((fav) => fav.test_id === testId);
    return favorite ? favorite.id : null;
  };

  const getFavoritesByType = (testType: string) => {
    return favorites.filter((fav) => fav.test_type === testType);
  };

  // Fetch favorites on mount
  useEffect(() => {
    console.log("useFavorites hook initialized, fetching favorites...");
    fetchFavorites();
  }, [fetchFavorites]);

  return {
    favorites,
    isLoading,
    addFavorite,
    removeFavorite,
    isFavorite,
    getFavoriteId,
    getFavoritesByType,
    refreshFavorites: fetchFavorites,
  };
};
